// Performance to plan (PRD §4.5, PP2, PP5–PP7, §3.7; extended by Chris, 2026-10-14): planned and
// reported hours, and the margin each gives, by week, month or quarter, for a project or a person.
//
// Everything is built day by day, the way the estimator costs a plan (§3.1):
// - Plan: each assignment's weekly hours spread over the week's working days, costed at the rate in
//   effect that day (the person's own, or the role's standard rate for a placeholder).
// - Actual: each reported entry's hours on its day, costed at the same person's rate that day. A
//   person who worked on the project without being on the plan is costed too (PP2).
// - Revenue and expenses: each month's recognized revenue and its non-billed expenses, spread over
//   the month's working days, the same for plan and actual. Margin = revenue − labor − expenses.
// By person, a project's revenue and expenses are shared by hours: the plan by planned hours, the
// actual by reported hours, in each month. So doing someone else's work in fewer hours at a higher
// rate shows as hours under plan and margin under plan, which is the §3.7 rate/mix effect.
//
// Rev. 1.16 (§3.11): reported hours are costed two ways. *Calculated* is the rate in effect that day,
// as before. *Actual* is the person's actual hourly rate for that month (actual-cost.ts): a flat-paid
// person who worked long hours costs each project less per hour. Without month costs (a caller
// without comp.read, or the Target basis) actual equals calculated. Under Target every hour, planned
// or reported, is costed at the target rate of the role the person fills on that project (§3.12).

import type { PersonMonthCost } from "./actual-cost";
import { addDays, eachDay, isWeekday, monthOf, type Iso, type Month } from "./dates";
import { rateOn, type RateStep } from "./engine";
import { expenseMonths, isTargetBasis, targetOrBase, type CostBasis, type CostRates, type Zoom } from "./estimate";
import { laborVariance, type LaborVariance } from "./margin";
import type { WorkingPlan } from "./projects";
import { expandTiers, recognizedByMonth } from "./revenue";

export interface PerfProject {
  id: string;
  code: string;
  plan: WorkingPlan;
}

/** One person's reported hours on one day toward one code (already split for two-code entries). */
export interface PerfActual {
  personId: string;
  day: Iso;
  code: string;
  hours: number;
}

export interface PerfInputs {
  projects: readonly PerfProject[];
  actuals: readonly PerfActual[];
  rates: CostRates;
  /** Each person's role, to cost them at the role rate when their own rate isn't visible. */
  roleOf: ReadonlyMap<string, string | undefined>;
  holidays: ReadonlySet<Iso>;
  basis: CostBasis;
  from: Iso;
  to: Iso;
  /** Each person's cost by month, keyed `${personId}|${month}` (§3.11). Absent: actual = calculated. */
  monthCosts?: ReadonlyMap<string, PersonMonthCost>;
  /**
   * Shared costs per hour (rev. 1.18), for the fully burdened view: the plan rate on a day, the actual
   * rate for a day's month, and who carries none. Absent: no shared costs.
   */
  shared?: { planRate: (day: Iso) => number; actualRate: (day: Iso) => number; carries: (personId: string) => boolean };
  /**
   * Actual non-labor cost from approved expense reports (ER6, rev. 1.21): project code → month →
   * PE8 category → amount, billable and internal lines left out. A month listed here replaces the
   * plan's costs for that project; a month not listed keeps the plan.
   */
  actualCosts?: ReadonlyMap<string, ReadonlyMap<Month, ReadonlyMap<string, number>>>;
}

export interface PerfPeriod {
  key: string;
  label: string;
  from: Iso;
  to: Iso;
  planHours: number;
  actualHours: number;
  planCost: number;
  /** Reported hours at each person's actual hourly rate for the month (§3.11). */
  actualCost: number;
  /** Reported hours at the calculated rate (§3.2–3.3). */
  calculatedCost: number;
  /** Flat-paid people's cost the projects don't carry, for a person or everyone (§3.11). */
  unreportedCost: number;
  /** Shared costs carried by the planned and the reported hours (rev. 1.18). */
  planShared: number;
  actualShared: number;
  revenue: number;
  planRevenue: number;
  actualRevenue: number;
  expenses: number;
  /** Other costs as spent: approved expense lines where a month has them, else the plan (ER6). */
  actualExpenses: number;
  planMargin: number;
  /** Null for a period with no reported hours yet. */
  actualMargin: number | null;
  calculatedMargin: number | null;
  planMarginPct: number | null;
  actualMarginPct: number | null;
  calculatedMarginPct: number | null;
}

export interface PerfResult {
  periods: PerfPeriod[];
  totals: Omit<PerfPeriod, "key" | "label" | "from" | "to">;
  variance: LaborVariance;
}

/** One project, one person, or everything combined (rev. 1.14): every project in the inputs, so every person on them. */
export type Focus = { by: "project"; projectId: string } | { by: "person"; personId: string } | { by: "all" };

interface Day {
  planH: number;
  planC: number;
  actH: number;
  /** Actual basis. */
  actC: number;
  /** Calculated basis. */
  calcC: number;
  unrep: number;
  planSh: number;
  actSh: number;
  /** By project: the project's revenue and expenses that day. By person: the share given to them. */
  planRev: number;
  actRev: number;
  planExp: number;
  actExp: number;
  revenue: number;
}

const blank = (): Day => ({ planH: 0, planC: 0, actH: 0, actC: 0, calcC: 0, unrep: 0, planSh: 0, actSh: 0, planRev: 0, actRev: 0, planExp: 0, actExp: 0, revenue: 0 });

const workingDays = (from: Iso, to: Iso, holidays: ReadonlySet<Iso>) => [...eachDay(from, to)].filter((d) => isWeekday(d) && !holidays.has(d));

function ratesFor(i: PerfInputs, personId: string | null, roleId: string | undefined): readonly RateStep[] {
  const role = roleId ? i.rates.roles[roleId] : undefined;
  // Target costs everyone at the role they fill (§3.12); a role with no target falls back to Base.
  if (i.basis === "target") return targetOrBase(role?.target, (personId ? i.rates.people?.[personId]?.base : undefined) ?? role?.base ?? []);
  if (i.basis === "targetOte") return targetOrBase(role?.targetOte, (personId ? i.rates.people?.[personId]?.ote : undefined) ?? role?.ote ?? []);
  const own = personId ? i.rates.people?.[personId] : undefined;
  return (own ?? role)?.[i.basis] ?? [];
}

/** The role a person fills on a project: their assignment's, else their own default role. */
function roleOn(p: PerfProject, personId: string, i: PerfInputs): string | undefined {
  return p.plan.assignments.find((a) => a.personId === personId)?.roleId ?? i.roleOf.get(personId);
}

/** Each day's planned hours and cost for one project, per person (null for placeholders). */
function planDays(p: PerfProject, i: PerfInputs): Map<Iso, Map<string, { h: number; c: number }>> {
  const out = new Map<Iso, Map<string, { h: number; c: number }>>();
  for (const a of p.plan.assignments) {
    const rates = ratesFor(i, a.personId, a.roleId);
    a.hours.forEach((weekly, w) => {
      if (!weekly) return;
      const monday = addDays(p.plan.startMonday, w * 7);
      let days = workingDays(monday, addDays(monday, 4), i.holidays);
      if (!days.length) days = [0, 1, 2, 3, 4].map((d) => addDays(monday, d));
      const perDay = weekly / days.length;
      for (const day of days) {
        if (day < i.from || day > i.to) continue;
        const byPerson = out.get(day) ?? new Map();
        const k = a.personId ?? `role:${a.roleId}`;
        const cur = byPerson.get(k) ?? { h: 0, c: 0 };
        byPerson.set(k, { h: cur.h + perDay, c: cur.c + perDay * rateOn(rates, day) });
        out.set(day, byPerson);
      }
    });
  }
  return out;
}

/** A project's recognized revenue and non-billed expenses per working day. */
function moneyDays(p: PerfProject, i: PerfInputs): Map<Iso, { rev: number; exp: number }> {
  const recognized = recognizedByMonth(expandTiers(p.plan.schedule).filter((l) => l.type !== "reimbursable"));
  const expenses = new Map<Month, number>();
  for (const e of p.plan.expenses) if (!e.reimbursable) for (const m of expenseMonths(e)) expenses.set(m, (expenses.get(m) ?? 0) + e.amount);
  const out = new Map<Iso, { rev: number; exp: number }>();
  for (const month of new Set([...recognized.keys(), ...expenses.keys()])) {
    const days = workingDays(`${month}-01`, lastDay(month), i.holidays);
    if (!days.length) continue;
    const rev = (recognized.get(month) ?? 0) / days.length;
    const exp = (expenses.get(month) ?? 0) / days.length;
    for (const d of days) if (d >= i.from && d <= i.to) out.set(d, { rev, exp });
  }
  return out;
}

function lastDay(month: Month): Iso {
  const [y, m] = month.split("-").map(Number);
  return `${month}-${String(new Date(Date.UTC(y, m, 0)).getUTCDate()).padStart(2, "0")}`;
}

/** The days of one focus: one project, or one person across every project. */
function daysFor(focus: Focus, i: PerfInputs): Map<Iso, Day> {
  const days = new Map<Iso, Day>();
  const at = (d: Iso) => {
    let x = days.get(d);
    if (!x) days.set(d, (x = blank()));
    return x;
  };
  const projects = focus.by === "project" ? i.projects.filter((p) => p.id === focus.projectId) : i.projects;
  // Combined, each project counts in full, as it does on its own; only a single person shares revenue by hours.
  const whole = focus.by !== "person";
  for (const p of projects) {
    const plan = planDays(p, i);
    const money = moneyDays(p, i);
    const actual = new Map<Iso, Map<string, number>>();
    for (const a of i.actuals) {
      if (a.code !== p.code || a.day < i.from || a.day > i.to) continue;
      const byPerson = actual.get(a.day) ?? new Map<string, number>();
      byPerson.set(a.personId, (byPerson.get(a.personId) ?? 0) + a.hours);
      actual.set(a.day, byPerson);
    }
    // Share revenue and expenses by month, by hours, for the person view.
    const monthShare = new Map<Month, { planAll: number; planMine: number; actAll: number; actMine: number }>();
    if (focus.by === "person") {
      for (const [d, byPerson] of plan) {
        const s = monthShare.get(monthOf(d)) ?? { planAll: 0, planMine: 0, actAll: 0, actMine: 0 };
        for (const [k, v] of byPerson) {
          s.planAll += v.h;
          if (k === focus.personId) s.planMine += v.h;
        }
        monthShare.set(monthOf(d), s);
      }
      for (const [d, byPerson] of actual) {
        const s = monthShare.get(monthOf(d)) ?? { planAll: 0, planMine: 0, actAll: 0, actMine: 0 };
        for (const [k, h] of byPerson) {
          s.actAll += h;
          if (k === focus.personId) s.actMine += h;
        }
        monthShare.set(monthOf(d), s);
      }
    }
    const mine = (k: string) => whole || (focus.by === "person" && k === focus.personId);
    for (const [d, byPerson] of plan) {
      for (const [k, v] of byPerson) {
        if (!mine(k)) continue;
        const x = at(d);
        x.planH += v.h;
        x.planC += v.c;
        // Placeholders carry shared costs; so does everyone not left out.
        if (i.shared && (k.startsWith("role:") || i.shared.carries(k))) x.planSh += v.h * i.shared.planRate(d);
      }
    }
    for (const [d, byPerson] of actual) {
      for (const [personId, h] of byPerson) {
        if (!mine(personId)) continue;
        const x = at(d);
        const calc = h * rateOn(ratesFor(i, personId, roleOn(p, personId, i)), d);
        const month = isTargetBasis(i.basis) ? undefined : i.monthCosts?.get(`${personId}|${monthOf(d)}`);
        x.actH += h;
        x.calcC += calc;
        x.actC += month ? h * month.rate : calc;
        if (i.shared?.carries(personId)) x.actSh += h * i.shared.actualRate(d);
      }
    }
    // Months with approved expense lines take them as the actual costs, spread over working days (ER6).
    const actualMonths = i.actualCosts?.get(p.code.toUpperCase());
    const actualDays = new Map<Iso, number>();
    for (const [month, byCat] of actualMonths ?? []) {
      const total = [...byCat.values()].reduce((t, v) => t + v, 0);
      const days = workingDays(`${month}-01`, lastDay(month), i.holidays);
      for (const d of days) if (d >= i.from && d <= i.to) actualDays.set(d, total / days.length);
    }
    const actualExpOn = (d: Iso, planned: number) => (actualMonths?.has(monthOf(d)) ? (actualDays.get(d) ?? 0) : planned);
    for (const [d, m] of money) {
      const x = at(d);
      if (whole) {
        x.revenue += m.rev;
        x.planRev += m.rev;
        x.actRev += m.rev;
        x.planExp += m.exp;
        x.actExp += actualExpOn(d, m.exp);
      } else {
        const s = monthShare.get(monthOf(d));
        if (!s) continue;
        const planShare = s.planAll ? s.planMine / s.planAll : 0;
        const actShare = s.actAll ? s.actMine / s.actAll : 0;
        x.revenue += m.rev * Math.max(planShare, actShare);
        x.planRev += m.rev * planShare;
        x.actRev += m.rev * actShare;
        x.planExp += m.exp * planShare;
        x.actExp += actualExpOn(d, m.exp) * actShare;
      }
    }
    // Actual costs on days the plan had no revenue or costs at all.
    for (const [d, amount] of actualDays) {
      if (money.has(d)) continue;
      if (whole) at(d).actExp += amount;
      else {
        const s = monthShare.get(monthOf(d));
        if (s?.actAll) at(d).actExp += (amount * s.actMine) / s.actAll;
      }
    }
  }
  // Unreported cost belongs to the business, so it shows for a person or everyone, never a project.
  if (focus.by !== "project" && i.monthCosts && !isTargetBasis(i.basis)) {
    for (const c of i.monthCosts.values()) {
      if (!c.unreported || (focus.by === "person" && c.personId !== focus.personId)) continue;
      const span = workingDays(`${c.month}-01`, c.through, i.holidays).filter((d) => d >= i.from && d <= i.to);
      for (const d of span) at(d).unrep += c.unreported / span.length;
    }
  }
  return days;
}

const quarterOf = (d: Iso) => `${d.slice(0, 4)}-Q${Math.floor((Number(d.slice(5, 7)) - 1) / 3) + 1}`;
const keyOf = (d: Iso, zoom: Zoom) => (zoom === "week" ? addDays(d, -((new Date(`${d}T00:00:00Z`).getUTCDay() + 6) % 7)) : zoom === "month" ? monthOf(d) : quarterOf(d));
/** YYYY-MM-DD (the week's Monday), YYYY-MM, or YYYY-Qn (Chris, 2026-10-14). */
const labelOf = (key: string, _zoom: Zoom) => key;

/** Performance for a focus over [from, to], grouped by week (Monday), month or quarter. */
export function performance(focus: Focus, i: PerfInputs, zoom: Zoom): PerfResult {
  const days = daysFor(focus, i);
  const byKey = new Map<string, { key: string; from: Iso; to: Iso; sum: Day }>();
  for (const d of eachDay(i.from, i.to)) {
    const key = keyOf(d, zoom);
    let p = byKey.get(key);
    if (!p) byKey.set(key, (p = { key, from: d, to: d, sum: blank() }));
    p.to = d;
    const x = days.get(d);
    if (x) for (const k of Object.keys(x) as (keyof Day)[]) p.sum[k] += x[k];
  }
  const periods: PerfPeriod[] = [...byKey.values()].map(({ key, from, to, sum: s }) => {
    const planMargin = s.planRev - s.planC - s.planExp;
    const actual = s.actH > 0 ? s.actRev - s.actC - s.actExp : null;
    const calculated = s.actH > 0 ? s.actRev - s.calcC - s.actExp : null;
    return {
      key,
      label: labelOf(key, zoom),
      from,
      to,
      planHours: s.planH,
      actualHours: s.actH,
      planCost: s.planC,
      actualCost: s.actC,
      calculatedCost: s.calcC,
      unreportedCost: s.unrep,
      planShared: s.planSh,
      actualShared: s.actSh,
      revenue: s.revenue,
      planRevenue: s.planRev,
      actualRevenue: s.actRev,
      expenses: s.planExp,
      actualExpenses: s.actExp,
      planMargin,
      actualMargin: actual,
      calculatedMargin: calculated,
      planMarginPct: s.planRev ? planMargin / s.planRev : null,
      actualMarginPct: actual !== null && s.actRev ? actual / s.actRev : null,
      calculatedMarginPct: calculated !== null && s.actRev ? calculated / s.actRev : null,
    };
  });
  const sum = (f: (p: PerfPeriod) => number) => periods.reduce((t, p) => t + f(p), 0);
  const reported = periods.filter((p) => p.actualMargin !== null);
  const planToDate = { h: reported.reduce((t, p) => t + p.planHours, 0), c: reported.reduce((t, p) => t + p.planCost, 0) };
  const actHours = sum((p) => p.actualHours);
  const actCost = sum((p) => p.actualCost);
  const totals = {
    planHours: sum((p) => p.planHours),
    actualHours: actHours,
    planCost: sum((p) => p.planCost),
    actualCost: actCost,
    calculatedCost: sum((p) => p.calculatedCost),
    unreportedCost: sum((p) => p.unreportedCost),
    planShared: sum((p) => p.planShared),
    actualShared: sum((p) => p.actualShared),
    revenue: sum((p) => p.revenue),
    planRevenue: sum((p) => p.planRevenue),
    actualRevenue: sum((p) => p.actualRevenue),
    expenses: sum((p) => p.expenses),
    actualExpenses: sum((p) => p.actualExpenses),
    planMargin: sum((p) => p.planMargin),
    actualMargin: reported.length ? reported.reduce((t, p) => t + (p.actualMargin ?? 0), 0) : null,
    calculatedMargin: reported.length ? reported.reduce((t, p) => t + (p.calculatedMargin ?? 0), 0) : null,
    planMarginPct: null as number | null,
    actualMarginPct: null as number | null,
    calculatedMarginPct: null as number | null,
  };
  totals.planMarginPct = totals.planRevenue ? totals.planMargin / totals.planRevenue : null;
  const reportedRevenue = reported.reduce((t, p) => t + p.actualRevenue, 0);
  totals.actualMarginPct = totals.actualMargin !== null && reportedRevenue ? totals.actualMargin / reportedRevenue : null;
  totals.calculatedMarginPct = totals.calculatedMargin !== null && reportedRevenue ? totals.calculatedMargin / reportedRevenue : null;
  // §3.7, over the periods with reported hours: hours on plan, profit off plan.
  const variance = laborVariance(planToDate.h, planToDate.h ? planToDate.c / planToDate.h : 0, actHours, actHours ? actCost / actHours : 0);
  return { periods, totals, variance };
}

// --- Who contributed (PP11, rev. 1.20) ---------------------------------------------------------------

/** One person's (or placeholder's) planned and reported hours and cost on one project in one period. */
export interface PerfContributor {
  period: string;
  projectId: string;
  code: string;
  /** A person's id, or `role:<roleId>` for a placeholder, which has a plan and never reported hours. */
  who: string;
  planHours: number;
  actualHours: number;
  planCost: number;
  /** At the person's actual rate for the month (§3.11), or calculated where that isn't available. */
  actualCost: number;
  calculatedCost: number;
}

/**
 * The same hours and cost as performance(), split by person and project in each period, so a
 * period's difference from plan can be traced to who drove it. Labor only: by project, revenue and
 * other costs are the same in plan and actual, so labor is the whole of the margin's difference.
 */
export function performanceContributors(focus: Focus, i: PerfInputs, zoom: Zoom): PerfContributor[] {
  const out = new Map<string, PerfContributor>();
  const at = (period: string, p: PerfProject, who: string) => {
    const k = `${period}|${p.id}|${who}`;
    let x = out.get(k);
    if (!x) out.set(k, (x = { period, projectId: p.id, code: p.code, who, planHours: 0, actualHours: 0, planCost: 0, actualCost: 0, calculatedCost: 0 }));
    return x;
  };
  const projects = focus.by === "project" ? i.projects.filter((p) => p.id === focus.projectId) : i.projects;
  const mine = (who: string) => focus.by !== "person" || who === focus.personId;
  for (const p of projects) {
    for (const [d, byPerson] of planDays(p, i)) {
      for (const [who, v] of byPerson) {
        if (!mine(who)) continue;
        const x = at(keyOf(d, zoom), p, who);
        x.planHours += v.h;
        x.planCost += v.c;
      }
    }
    for (const a of i.actuals) {
      if (a.code !== p.code || a.day < i.from || a.day > i.to || !mine(a.personId)) continue;
      const calc = a.hours * rateOn(ratesFor(i, a.personId, roleOn(p, a.personId, i)), a.day);
      const month = isTargetBasis(i.basis) ? undefined : i.monthCosts?.get(`${a.personId}|${monthOf(a.day)}`);
      const x = at(keyOf(a.day, zoom), p, a.personId);
      x.actualHours += a.hours;
      x.calculatedCost += calc;
      x.actualCost += month ? a.hours * month.rate : calc;
    }
  }
  return [...out.values()].filter((x) => x.planHours > 1e-9 || x.actualHours > 1e-9);
}

/**
 * A cost difference split as §3.7 does: hours (actual − planned hours, at the planned rate) and rate
 * and mix (the rest). Unplanned hours are all hours; a planned row with no reported hours is all hours.
 */
export function splitCostDifference(planHours: number, planCost: number, actualHours: number, actualCost: number): { hours: number; rate: number; total: number } {
  const total = actualCost - planCost;
  const planRate = planHours ? planCost / planHours : actualHours ? actualCost / actualHours : 0;
  const hours = (actualHours - planHours) * planRate;
  return { hours, rate: total - hours, total };
}


/** A project's other (non-labor) costs in one period, plan against actual, by category (PP11, ER6). */
export interface PerfCostRow {
  period: string;
  projectId: string;
  code: string;
  category: string;
  plan: number;
  actual: number;
}

/**
 * Other costs by project and category in each period, beside the people (PP11). The plan is the
 * project's non-billed costs (PE8); the actual is approved expense lines where the month has any,
 * else the plan. Projects only: by person, other costs are shared by hours and not split out.
 */
export function performanceCosts(focus: Focus, i: PerfInputs, zoom: Zoom): PerfCostRow[] {
  if (focus.by === "person") return [];
  const out = new Map<string, PerfCostRow>();
  const add = (period: string, p: PerfProject, category: string, field: "plan" | "actual", amount: number) => {
    if (!amount) return;
    const k = `${period}|${p.id}|${category}`;
    const row = out.get(k) ?? { period, projectId: p.id, code: p.code, category, plan: 0, actual: 0 };
    row[field] += amount;
    out.set(k, row);
  };
  const projects = focus.by === "project" ? i.projects.filter((p) => p.id === focus.projectId) : i.projects;
  for (const p of projects) {
    const planned = new Map<Month, Map<string, number>>();
    for (const e of p.plan.expenses) {
      if (e.reimbursable) continue;
      for (const m of expenseMonths(e)) {
        const byCat = planned.get(m) ?? new Map<string, number>();
        byCat.set(e.category, (byCat.get(e.category) ?? 0) + e.amount);
        planned.set(m, byCat);
      }
    }
    const actual = i.actualCosts?.get(p.code.toUpperCase());
    for (const month of new Set([...planned.keys(), ...(actual?.keys() ?? [])])) {
      const days = workingDays(`${month}-01`, lastDay(month), i.holidays);
      const inRange = days.filter((d) => d >= i.from && d <= i.to);
      if (!days.length || !inRange.length) continue;
      for (const d of inRange) {
        const period = keyOf(d, zoom);
        for (const [cat, amount] of planned.get(month) ?? []) add(period, p, cat, "plan", amount / days.length);
        const source = actual?.has(month) ? actual.get(month)! : planned.get(month);
        for (const [cat, amount] of source ?? []) add(period, p, cat, "actual", amount / days.length);
      }
    }
  }
  return [...out.values()];
}
